import { useLocation, useParams } from "react-router-dom";
import buildRoute from "components/buildRoute";
import PageHeader from "components/PageHeader";
import { canContribute } from "../hartUtils";
import { useModelDetails } from "../hartHooks";
import { Button, Box, Paper } from "@mui/material";
import ClientFormFields, { validateClient, clientNullableFields } from "../components/ClientFormFields";
import AssociationsSection from "../components/associations";

// ========================================
// MODEL CONFIG
// ========================================
const MODEL_CONFIG = {
  apiEndpoint: '/hart/clients',
  idParamName: 'clientId',
  displayName: 'Client',
  displayField: 'name',
  FormComponent: ClientFormFields,
  dataKey: 'client',
  validateFn: validateClient,
  nullableFields: clientNullableFields,
};
// ========================================

export const ClientDetailsPage = () => {
  const { projectId, [MODEL_CONFIG.idParamName]: idParam } = useParams();
  const location = useLocation();

  const {
    data,
    setData,
    editable,
    submitting,
    loading,
    error,
    validationErrors,
    handleSubmit,
    handleEditToggle,
    handleBack,
  } = useModelDetails({
    apiEndpoint: MODEL_CONFIG.apiEndpoint,
    idParam,
    initialData: location.state?.[MODEL_CONFIG.dataKey],
    validateFn: MODEL_CONFIG.validateFn,
    nullableFields: MODEL_CONFIG.nullableFields,
  });

  const canEditRole = canContribute(projectId);
  const { FormComponent, displayName, displayField } = MODEL_CONFIG;

  if (loading) return <p>Loading {displayName} details...</p>;
  if (error) return <p>Error loading {displayName}: {error}</p>;

  return (
    <>
      <PageHeader
        title={`${displayName}: ${displayField ? (data[displayField] || idParam) : idParam}`}
        action={
          canEditRole && (
            <Button
              variant="contained"
              color={editable ? "secondary" : "primary"}
              aria-label={`Edit ${displayName}`}
              onClick={handleEditToggle}
              sx={{ '&:hover': { backgroundColor: 'secondary.main' } }}
            >
              {editable ? "Cancel Edit" : `Edit ${displayName}`}
            </Button>
          )
        }
      />
      <Paper sx={{ p: 3, mb: 3, mt: 5 }}>
        <FormComponent data={data} editable={editable} onChange={setData} errors={validationErrors} displayName={displayName} projectId={projectId} />
      </Paper>

      {/* Associations Section */}
      {!loading && data && data.org_id && (
        <AssociationsSection
          parentId={data.org_id}
          parentData={data}
          parentType="client"
          associations={['client_contacts', 'jobs']}
          editable={editable}
          editableAssociationCallbacks={{
            client_contacts: (contacts) => setData(prev => ({
              ...prev,
              client_contacts: contacts,
              // Extract IDs for writable field
              client_contact: contacts.map(c => c.id).filter(Boolean),
            })),
            jobs: (jobs) => setData(prev => ({
              ...prev,
              jobs,
              // Extract job_nos for writable field
              job: jobs.map(j => j.job_no).filter(Boolean),
            })),
          }}
        />
      )}

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="outlined" onClick={handleBack}>
          Back
        </Button>
        {editable && canEditRole && (
          <Button
            variant="contained"
            color="success"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Saving..." : "Submit"}
          </Button>
        )}
      </Box>
    </>
  );
};

const routeArgs = {
  element: <ClientDetailsPage />,
};

export default buildRoute(routeArgs);
